import { LangLink } from "@/components/LangLink";
import { Seo } from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import {
  Ruler, Layers, Shirt, Shield, Link2, Wind, Tag, Info, ArrowRight,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || "";

// Reference sizing for the Kentucky / Dy'on ranges. Charts are guidance — when a
// horse is between sizes we recommend sizing up.
const RUG_ROWS = [
  ["4'9\"", "105 cm", "Small pony"],
  ["5'0\"", "115 cm", "Pony 12–13hh"],
  ["5'3\"", "125 cm", "Pony 13–14hh"],
  ["5'6\"", "135 cm", "Small horse 14–15hh"],
  ["5'9\"", "145 cm", "Horse 15–15.2hh"],
  ["6'0\"", "155 cm", "Horse 15.2–16hh"],
  ["6'3\"", "160 cm", "Horse 16–16.2hh"],
  ["6'6\"", "165 cm", "Horse 16.2–17hh"],
  ["6'9\"", "175 cm", "Large horse 17hh+"],
  ["7'0\"", "185 cm", "Warmblood / draft"],
  ["7'3\"", "195 cm", "Extra large / draft"],
];

const RUG_WEIGHTS = [
  ["No-fill / Rain sheet", "0 g", "Dry wind protection, mild days"],
  ["Lightweight", "50–100 g", "Cool, changeable weather"],
  ["Medium", "150–200 g", "The all-rounder for Irish winters"],
  ["Heavyweight", "300–400 g", "Cold snaps, clipped horses"],
];

const BOOT_ROWS = [
  ["Pony", "Up to ~13.2hh", "Brushing, tendon, fetlock & travel boots"],
  ["Cob", "~13.2–15.2hh", "Brushing, tendon, fetlock & travel boots"],
  ["Full", "~15.2–16.3hh", "Most common adult horse size"],
  ["X-Full / XL", "16.3hh+ / Warmblood", "Larger bone & cannon circumference"],
];

const BRIDLE_ROWS = [
  ["Pony", "Pony / small cob", "Dy'on, Kentucky anatomic bridles"],
  ["Cob", "Cobs & smaller horses", "Dy'on, Kentucky anatomic bridles"],
  ["Full", "Standard horse / TB", "Dy'on, Kentucky anatomic bridles"],
  ["Oversize / Warmblood", "Large warmblood / draft heads", "Select where available"],
];

const HEADCOLLAR_ROWS = [
  ["Foal", "Foals & weanlings"],
  ["Pony", "Ponies up to ~13.2hh"],
  ["Cob", "Cobs ~13.2–15.2hh"],
  ["Full", "Horses ~15.2–16.3hh"],
  ["Warmblood", "Large warmbloods 16.3hh+"],
];

const VEIL_ROWS = [
  ["Pony", "Ponies & small cobs"],
  ["Cob", "Cobs & smaller horses"],
  ["Full", "Standard horse size"],
];

const PAD_ROWS = [
  ["Dressage", "Long, straight-cut panel for dressage saddles", "Pony & Full"],
  ["Jumping", "Forward-cut panel for jumping / GP saddles", "Pony & Full"],
  ["Half pad", "Shaped correction & comfort pad", "One size (trim to fit)"],
];

const SECTIONS = [
  { id: "rugs", label: "Rugs", icon: Layers },
  { id: "pads", label: "Saddle Pads", icon: Shirt },
  { id: "boots", label: "Boots", icon: Shield },
  { id: "bridles", label: "Bridles", icon: Link2 },
  { id: "headcollars", label: "Headcollars", icon: Tag },
  { id: "veils", label: "Fly Veils", icon: Wind },
];

const ChartCard = ({ id, icon: Icon, title, intro, children }) => (
  <section id={id} className="scroll-mt-28" data-testid={`size-section-${id}`}>
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
        <Icon className="h-5 w-5" />
      </span>
      <h2 className="font-display text-2xl">{title}</h2>
    </div>
    {intro && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{intro}</p>}
    <div className="mt-4 overflow-x-auto rounded-xl border border-border">
      {children}
    </div>
  </section>
);

export default function SizeGuide() {
  const { tx } = useI18n();

  const faqs = [
    ["How do I measure my horse for a rug?",
      "Measure in a straight line from the centre of the chest, along the side of the body, to the point of the buttock (the back edge of the hindquarter). That measurement is your rug size — shown in feet/inches (UK) or centimetres (EU)."],
    ["What if my horse is between sizes?",
      "Size up. A slightly larger rug, boot or bridle is more comfortable and less likely to rub than one that is too tight."],
    ["Do Kentucky rugs come in cm or feet?",
      "Kentucky uses the continental centimetre system. Use the conversion chart on this page to match your usual UK feet/inches size to the correct cm size."],
  ];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(([q, a]) => ({
      "@type": "Question", name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <>
      <Seo
        title="Size Guide | Rugs, Boots, Bridles & Saddle Pads | Boomerang.ie"
        description="Kentucky Horsewear & Dy'on size guide: horse rug cm-to-feet conversions, rug weights, boot, bridle, headcollar, fly veil and saddle pad sizing. How to measure your horse correctly."
        canonical={`${SITE_URL}/size-guide`}
        jsonLd={jsonLd}
      />

      <div className="container-site py-8 lg:py-12" data-testid="size-guide-page">
        {/* Header */}
        <header className="max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">{tx("Fit & Sizing")}</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">{tx("Size Guide")}</h1>
          <p className="mt-4 text-muted-foreground">
            {tx("Find the perfect fit across the Kentucky Horsewear and Dy'on ranges — from turnout rugs to bridles. Charts are a guide; when in doubt between two sizes, size up.")}
          </p>
        </header>

        {/* Quick nav */}
        <nav className="mt-8 flex flex-wrap gap-2" aria-label="Size guide sections">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`size-nav-${s.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium transition-colors hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
            >
              <s.icon className="h-3.5 w-3.5" /> {tx(s.label)}
            </a>
          ))}
        </nav>

        {/* How to measure */}
        <div className="mt-8 flex items-start gap-3 rounded-xl border border-dashed border-border bg-secondary/40 p-4">
          <Ruler className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--primary))]" />
          <div>
            <h3 className="text-sm font-semibold">{tx("How to measure for a rug")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {tx("Measure in a straight line from the centre of the chest, along the side, to the point of the buttock. Use the chart below to convert between UK feet/inches and EU centimetres.")}
            </p>
          </div>
        </div>

        <div className="mt-10 space-y-12">
          {/* RUGS */}
          <ChartCard id="rugs" icon={Layers} title={tx("Horse Rugs — Size Conversion")}
            intro={tx("Turnout, stable and cooler rugs. Match your usual UK size to the Kentucky cm size.")}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tx("UK Size (ft/in)")}</TableHead>
                  <TableHead>{tx("EU Size (cm)")}</TableHead>
                  <TableHead>{tx("Typical horse")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {RUG_ROWS.map((r) => (
                  <TableRow key={r[1]}>
                    <TableCell className="font-medium">{r[0]}</TableCell>
                    <TableCell className="font-mono">{r[1]}</TableCell>
                    <TableCell className="text-muted-foreground">{tx(r[2])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ChartCard>

          <section className="scroll-mt-28">
            <h2 className="font-display text-xl">{tx("Rug Weights (Fill Guide)")}</h2>
            <div className="mt-4 overflow-x-auto rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tx("Type")}</TableHead>
                    <TableHead>{tx("Fill")}</TableHead>
                    <TableHead>{tx("Best for")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {RUG_WEIGHTS.map((r) => (
                    <TableRow key={r[0]}>
                      <TableCell className="font-medium">{tx(r[0])}</TableCell>
                      <TableCell className="font-mono">{r[1]}</TableCell>
                      <TableCell className="text-muted-foreground">{tx(r[2])}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          {/* SADDLE PADS */}
          <ChartCard id="pads" icon={Shirt} title={tx("Saddle Pads & Numnahs")}
            intro={tx("Choose the cut to match your saddle, then the size to match your horse.")}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tx("Cut")}</TableHead>
                  <TableHead>{tx("Use")}</TableHead>
                  <TableHead>{tx("Sizes")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {PAD_ROWS.map((r) => (
                  <TableRow key={r[0]}>
                    <TableCell className="font-medium">{tx(r[0])}</TableCell>
                    <TableCell className="text-muted-foreground">{tx(r[1])}</TableCell>
                    <TableCell>{tx(r[2])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ChartCard>

          {/* BOOTS */}
          <ChartCard id="boots" icon={Shield} title={tx("Boots & Leg Protection")}
            intro={tx("Brushing, tendon, fetlock and travel boots. Larger-boned horses may need to size up.")}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tx("Size")}</TableHead>
                  <TableHead>{tx("Approx. height")}</TableHead>
                  <TableHead>{tx("Notes")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {BOOT_ROWS.map((r) => (
                  <TableRow key={r[0]}>
                    <TableCell className="font-medium">{tx(r[0])}</TableCell>
                    <TableCell className="font-mono">{r[1]}</TableCell>
                    <TableCell className="text-muted-foreground">{tx(r[2])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ChartCard>

          {/* BRIDLES */}
          <ChartCard id="bridles" icon={Link2} title={tx("Bridles")}
            intro={tx("Dy'on and Kentucky anatomic bridles. If your horse has a particularly large or fine head, choose the relevant size.")}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tx("Size")}</TableHead>
                  <TableHead>{tx("Suits")}</TableHead>
                  <TableHead>{tx("Range")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {BRIDLE_ROWS.map((r) => (
                  <TableRow key={r[0]}>
                    <TableCell className="font-medium">{tx(r[0])}</TableCell>
                    <TableCell className="text-muted-foreground">{tx(r[1])}</TableCell>
                    <TableCell>{tx(r[2])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ChartCard>

          {/* HEADCOLLARS */}
          <ChartCard id="headcollars" icon={Tag} title={tx("Headcollars & Halters")}
            intro={tx("Adjustable headcollars in the standard equestrian size range.")}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tx("Size")}</TableHead>
                  <TableHead>{tx("Suits")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {HEADCOLLAR_ROWS.map((r) => (
                  <TableRow key={r[0]}>
                    <TableCell className="font-medium">{tx(r[0])}</TableCell>
                    <TableCell className="text-muted-foreground">{tx(r[1])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ChartCard>

          {/* FLY VEILS */}
          <ChartCard id="veils" icon={Wind} title={tx("Fly Veils & Ear Bonnets")}
            intro={tx("Ear bonnets and fly veils stretch to fit — choose the closest size to your horse.")}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tx("Size")}</TableHead>
                  <TableHead>{tx("Suits")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {VEIL_ROWS.map((r) => (
                  <TableRow key={r[0]}>
                    <TableCell className="font-medium">{tx(r[0])}</TableCell>
                    <TableCell className="text-muted-foreground">{tx(r[1])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ChartCard>
        </div>

        {/* FAQ */}
        <section className="mt-14 max-w-2xl">
          <h2 className="font-display text-2xl">{tx("Sizing FAQs")}</h2>
          <dl className="mt-4 space-y-5">
            {faqs.map(([q, a]) => (
              <div key={q}>
                <dt className="font-medium">{tx(q)}</dt>
                <dd className="mt-1 text-sm text-muted-foreground">{tx(a)}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* CTA */}
        <div className="mt-12 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-6">
          <Info className="h-5 w-5 text-[hsl(var(--primary))]" />
          <p className="flex-1 text-sm text-muted-foreground">
            {tx("Still unsure about a size? Get in touch and our team will help you choose.")}
          </p>
          <LangLink to="/contact" data-testid="size-guide-contact-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--primary-foreground))] transition-transform hover:translate-x-0.5">
            {tx("Contact us")} <ArrowRight className="h-4 w-4" />
          </LangLink>
        </div>
      </div>
    </>
  );
}
